import React, { useCallback, useEffect, useState } from "react";
import { useTelegram } from "../../../hooks/useTelegram";
import TelegramScreen from "../../../components/kit/Screen/TelegramScreen";
import TelegramHeader from "../../../components/kit/Header/TelegramHeader";
import TelegramText from "../../../components/kit/Text/TelegramText";
import { useNavigate } from "react-router-dom";

import CheckoutCard from "../../../components/kit/CheckoutCard/CheckoutCard";
import {
  PATH_BRAND,
  PATH_CATEGORY,
  PATH_PRODUCT,
  PATH_PRODUCT_TYPE,
  PATH_USER_DETAIL_EDIT,
} from "../../../constants/Paths";
import { useDispatch, useSelector } from "react-redux";
import {
  addToCart,
  removeFromCart,
} from "../../../components/kit/Redux/Actions";
import {
  areCartItemsSame,
  reviewOrder,
} from "../../../components/utils/review-order";
import { useAlert } from "../../../components/providers/AlertProvider";
import StoreIndicator from "../../../components/kit/StoreIndicator/StoreIndicator";
import TelegramButton from "../../../components/kit/Button/TelegramButton";

const OrderReviewScreen = () => {
  const { webApp, user } = useTelegram();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cartItems = useSelector((state) => state.cartItems.cartItems);

  // Debug logging for cart items
  useEffect(() => {
    if (cartItems.length > 0) {
      const itemsInfo = cartItems
        .map(
          (item, index) =>
            `${item.title}: qty=${item.quantity || 0}, ordered=${
              item.orderedqty || 0
            }, available=${item.availableQty || 0}`
        )
        .join("\n");
      console.log(`[OrderReviewScreen] Cart State Changed:\n${itemsInfo}`);
    }
  }, [cartItems]);

  const checkoutComment = useSelector((state) => state.checkoutComment);
  const totalPrice = cartItems.reduce((a, c) => a + c.price * c.quantity, 0);
  const totalItems = cartItems.reduce((a, c) => a + c.quantity, 0);

  const sessionToken = useSelector((state) => state.sessionToken);
  const surchargeChargeList = useSelector((state) => state.surchargeChargeList);
  const deliveryChargeList = useSelector((state) => state.deliveryChargeList);
  const exclusivePromotionChargeList = useSelector(
    (state) => state.exclusivePromotionChargeList
  );
  const existingOrder = useSelector((state) => state.existingOrder);
  const existingOrderCartItems = useSelector(
    (state) => state.existingOrderCartItems
  );
  const selectedBrand = useSelector((state) => state.selectedBrand);
  const selectedProductType = useSelector((state) => state.selectedProductType);
  const selectedCategory = useSelector((state) => state.selectedCategory);
  const selectedStore = useSelector((state) => state.selectedStore);

  const { triggerAlert } = useAlert();

  // This id will be used by our backend to send messages to the chat with the user
  const queryId = webApp.initDataUnsafe?.query_id;

  const onMainClick = useCallback(async () => {
    try {
      if (areCartItemsSame(cartItems, existingOrderCartItems)) {
        triggerAlert("Cart", "No changes made to the cart.");
        // alert(`No changes made to the cart.`);
        return;
      }
      const utils = { webApp, queryId, sessionToken, dispatch, navigate };
      const userId = user?.id;
      const {
        customerName,
        customerContact,
        customerAddress,
        timeSlot,
        agentId,
      } = existingOrder;
      const userDetails = {
        userId,
        agentId,
        customerName,
        customerContact,
        customerAddress,
        timeSlot,
        surchargeChargeList,
        deliveryChargeList,
        exclusivePromotionChargeList,
      };
      await reviewOrder(
        utils,
        userDetails,
        cartItems,
        existingOrderCartItems,
        selectedStore
      );
    } catch (error) {
      alert(error);
      throw error;
    }
  }, [
    webApp,
    queryId,
    sessionToken,
    dispatch,
    navigate,
    user,
    existingOrder,
    existingOrderCartItems,
    surchargeChargeList,
    deliveryChargeList,
    exclusivePromotionChargeList,
    cartItems,
    triggerAlert,
    selectedStore,
  ]);

  const onBackClick = useCallback(() => {
    if (selectedCategory) {
      navigate(PATH_PRODUCT, {
        replace: true,
      });
      return;
    }

    if (selectedBrand) {
      navigate(PATH_CATEGORY, {
        replace: true,
      });
      return;
    }

    if (selectedProductType) {
      navigate(PATH_BRAND, {
        replace: true,
      });
      return;
    }

    navigate(PATH_PRODUCT_TYPE, {
      replace: true,
    });
  }, [navigate, selectedCategory, selectedBrand, selectedProductType]);

  useEffect(() => {
    webApp.ready();
    webApp.MainButton.onClick(onMainClick);
    webApp.BackButton.onClick(onBackClick);

    webApp.MainButton.text = `CHECKOUT`;
    webApp.MainButton.color = "#31b545";

    webApp.BackButton.show();

    if (cartItems.length > 0) {
      webApp.MainButton.show();
    } else {
      webApp.MainButton.hide();
    }

    return () => {
      webApp.MainButton.offClick(onMainClick);
      webApp.BackButton.offClick(onBackClick);
    };
  }, [
    webApp,
    cartItems,
    onMainClick,
    onBackClick,
    totalPrice,
    checkoutComment,
  ]);

  const onAdd = (item, newAddQty) => {
    console.log(
      `[OrderReviewScreen] onAdd received: ${item.title}, adding quantity: ${newAddQty}`
    );
    dispatch(addToCart(item, newAddQty));
  };

  const onRemove = (item, newSubQty) => {
    console.log(
      `[OrderReviewScreen] onRemove received: ${item.title}, removing quantity: ${newSubQty}`
    );
    dispatch(removeFromCart(item, newSubQty));
  };

  // Navigate to user detail edit screen
  const onEditUserDetailsClick = useCallback(() => {
    console.log(
      "[OrderReviewScreen] Navigating to user detail edit with order:",
      existingOrder
    );

    if (!existingOrder || !existingOrder.id) {
      triggerAlert(
        "Error: No order data available for editing customer details"
      );
      return;
    }

    navigate(PATH_USER_DETAIL_EDIT, {
      state: {
        orderData: existingOrder,
      },
    });
  }, [navigate, existingOrder, triggerAlert]);

  return (
    <TelegramScreen>
      <StoreIndicator />
      <div>
        <TelegramHeader>
          <TelegramText className={"telegramTitle"}>
            REVIEW TROLLEY
          </TelegramText>
        </TelegramHeader>

        {cartItems.length > 0 ? (
          <div className="checkout__container">
            <div className="checkout__cards__container">
              {cartItems.map((item) => {
                return (
                  <CheckoutCard
                    food={item}
                    quantity={(item.orderedqty || 0) + (item.quantity || 0)}
                    availableQty={item.availableQty || 0}
                    key={item.id}
                    onAdd={onAdd}
                    onRemove={onRemove}
                  />
                );
              })}
            </div>

            <div className="checkout__total__items__container">
              <TelegramText>Total Items:</TelegramText>
              <TelegramText>
                <span>{totalItems}</span>
              </TelegramText>
            </div>
            <div className="checkout__cost__container">
              <TelegramText>Total Cost:</TelegramText>
              <TelegramText>
                <span>${totalPrice.toFixed(2)}</span>
              </TelegramText>
            </div>

            {/* Customer Details Edit Button */}
            {/* Commented out on 30 May to prevent users from changing customer detail */}
            {/* {existingOrder && existingOrder.id && (
              <div className="edit-details-button-container">
                <TelegramButton 
                  onClick={onEditUserDetailsClick}
                  className="edit-user-details-button"
                >
                  ✏️ Edit Customer Details
                </TelegramButton>
              </div>
            )} */}
          </div>
        ) : (
          <TelegramText className={"centered-text"}>Empty Cart</TelegramText>
        )}
      </div>
    </TelegramScreen>
  );
};

export default OrderReviewScreen;
